import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Star, Plus, Trash2, Eye, MousePointer, Package, Sparkles, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCuration() {
  const [activeTab, setActiveTab] = useState('featured');
  const [featuredDeals, setFeaturedDeals] = useState([]);
  const [collections, setCollections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Feature deal modal state
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [featureForm, setFeatureForm] = useState({
    priority: '1',
    display_days: '7',
    notes: ''
  });
  const [featureLoading, setFeatureLoading] = useState(false);

  // Collection modal state
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionForm, setCollectionForm] = useState({
    name: '',
    description: '',
    display_order: '1',
    active_days: ''
  });
  const [collectionLoading, setCollectionLoading] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [featuredRes, collectionsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/curation/featured?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/curation/collections?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/curation/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (featuredRes.ok) {
        const data = await featuredRes.json();
        setFeaturedDeals(data.featured || []);
      }
      if (collectionsRes.ok) {
        const data = await collectionsRes.json();
        setCollections(data.collections || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching curation data:', error);
      toast.error('Failed to load curation data');
    } finally {
      setLoading(false);
    }
  };

  const searchItems = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/rshd/items?status=available&limit=20&search=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.items || data || []);
      }
    } catch (error) {
      console.error('Error searching items:', error);
      toast.error('Failed to search items');
    } finally {
      setSearching(false);
    }
  };

  const featureDeal = async () => {
    if (!selectedItem) return;
    
    setFeatureLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/curation/featured`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          item_id: selectedItem.id,
          priority: parseInt(featureForm.priority),
          display_days: parseInt(featureForm.display_days),
          notes: featureForm.notes || null
        })
      });

      if (res.ok) {
        toast.success(`${selectedItem.name} is now featured!`);
        setShowFeatureModal(false);
        setSelectedItem(null);
        setSearchResults([]);
        setSearchQuery('');
        setFeatureForm({ priority: '1', display_days: '7', notes: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to feature deal');
      }
    } catch (error) {
      console.error('Error featuring deal:', error);
      toast.error('Failed to feature deal');
    } finally {
      setFeatureLoading(false);
    }
  };

  const unfeatureDeal = async (itemId) => {
    try {
      const res = await fetch(`${API_URL}/api/curation/featured/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Deal unfeatured');
        fetchData();
      } else {
        toast.error('Failed to unfeature deal');
      }
    } catch (error) {
      console.error('Error unfeaturing deal:', error);
      toast.error('Failed to unfeature deal');
    }
  };

  const createCollection = async () => {
    if (!collectionForm.name.trim()) {
      toast.error('Collection name is required');
      return;
    }
    
    setCollectionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/curation/collections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: collectionForm.name,
          description: collectionForm.description,
          display_order: parseInt(collectionForm.display_order) || 1,
          active_days: collectionForm.active_days ? parseInt(collectionForm.active_days) : null
        })
      });

      if (res.ok) {
        toast.success('Collection created!');
        setShowCollectionModal(false);
        setCollectionForm({ name: '', description: '', display_order: '1', active_days: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create collection');
      }
    } catch (error) {
      console.error('Error creating collection:', error);
      toast.error('Failed to create collection');
    } finally {
      setCollectionLoading(false);
    }
  };

  const deleteCollection = async (collectionId) => {
    try {
      const res = await fetch(`${API_URL}/api/curation/collections/${collectionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Collection deleted');
        fetchData();
      } else {
        toast.error('Failed to delete collection');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast.error('Failed to delete collection');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manual Curation</h1>
          <p className="text-gray-500">Feature deals and create themed collections</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Star className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.featured_deals?.active_count || 0}</p>
                  <p className="text-sm text-gray-500">Featured Deals</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.featured_deals?.total_views || 0}</p>
                  <p className="text-sm text-gray-500">Total Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MousePointer className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.featured_deals?.ctr || 0}%</p>
                  <p className="text-sm text-gray-500">Click-Through Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.collections?.active_count || 0}</p>
                  <p className="text-sm text-gray-500">Collections</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="featured" className="flex items-center gap-2">
            <Star className="h-4 w-4" />
            Featured Deals
          </TabsTrigger>
          <TabsTrigger value="collections" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Collections
          </TabsTrigger>
        </TabsList>

        {/* Featured Deals Tab */}
        <TabsContent value="featured" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Featured Deals</CardTitle>
                  <CardDescription>Highlight deals on the homepage</CardDescription>
                </div>
                <Dialog open={showFeatureModal} onOpenChange={setShowFeatureModal}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Feature a Deal
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>Feature a Deal</DialogTitle>
                      <DialogDescription>
                        Search for a deal to feature on the homepage
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      {/* Search */}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Search deals by name..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && searchItems()}
                        />
                        <Button onClick={searchItems} disabled={searching}>
                          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        </Button>
                      </div>

                      {/* Search Results */}
                      {searchResults.length > 0 && (
                        <div className="border rounded-lg max-h-48 overflow-y-auto">
                          {searchResults.map((item) => (
                            <div
                              key={item.id}
                              className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between border-b last:border-b-0 ${selectedItem?.id === item.id ? 'bg-emerald-50' : ''}`}
                              onClick={() => setSelectedItem(item)}
                            >
                              <div>
                                <p className="font-medium text-sm">{item.name}</p>
                                <p className="text-xs text-gray-500">{item.drlp_name} • ${item.deal_price}</p>
                              </div>
                              {selectedItem?.id === item.id && (
                                <Badge className="bg-emerald-100 text-emerald-700">Selected</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedItem && (
                        <>
                          <div className="p-3 bg-emerald-50 rounded-lg">
                            <p className="font-medium">{selectedItem.name}</p>
                            <p className="text-sm text-gray-600">{selectedItem.drlp_name}</p>
                            <p className="text-sm font-bold text-emerald-600">${selectedItem.deal_price}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Priority (1 = highest)</Label>
                              <Select value={featureForm.priority} onValueChange={(v) => setFeatureForm({ ...featureForm, priority: v })}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1 - Top Priority</SelectItem>
                                  <SelectItem value="2">2 - High</SelectItem>
                                  <SelectItem value="3">3 - Normal</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Display Duration</Label>
                              <Select value={featureForm.display_days} onValueChange={(v) => setFeatureForm({ ...featureForm, display_days: v })}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="3">3 Days</SelectItem>
                                  <SelectItem value="7">7 Days</SelectItem>
                                  <SelectItem value="14">14 Days</SelectItem>
                                  <SelectItem value="30">30 Days</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Textarea
                              placeholder="Why is this deal being featured?"
                              value={featureForm.notes}
                              onChange={(e) => setFeatureForm({ ...featureForm, notes: e.target.value })}
                            />
                          </div>
                        </>
                      )}
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowFeatureModal(false)}>Cancel</Button>
                      <Button 
                        onClick={featureDeal} 
                        disabled={!selectedItem || featureLoading}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {featureLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                        Feature Deal
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {featuredDeals.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Star className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No featured deals yet</p>
                  <p className="text-sm">Feature deals to highlight them on the homepage</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {featuredDeals.map((featured) => (
                    <div key={featured.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full">
                          <span className="text-sm font-bold text-yellow-600">#{featured.priority}</span>
                        </div>
                        <div>
                          <p className="font-medium">{featured.item?.name || featured.item_snapshot?.name}</p>
                          <p className="text-sm text-gray-500">
                            {featured.item?.drlp_name || featured.item_snapshot?.drlp_name} • 
                            ${featured.item?.deal_price || featured.item_snapshot?.deal_price}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-gray-500">{featured.views || 0} views • {featured.clicks || 0} clicks</p>
                          <p className="text-xs text-gray-400">Expires: {new Date(featured.display_until).toLocaleDateString()}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => unfeatureDeal(featured.item_id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Collections Tab */}
        <TabsContent value="collections" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Curated Collections</CardTitle>
                  <CardDescription>Group deals into themed collections</CardDescription>
                </div>
                <Dialog open={showCollectionModal} onOpenChange={setShowCollectionModal}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Collection
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>Create Collection</DialogTitle>
                      <DialogDescription>
                        Create a themed collection of deals (e.g., "BBQ Essentials", "Back to School")
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Collection Name *</Label>
                        <Input
                          placeholder="e.g., Weekend BBQ Essentials"
                          value={collectionForm.name}
                          onChange={(e) => setCollectionForm({ ...collectionForm, name: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          placeholder="Describe this collection..."
                          value={collectionForm.description}
                          onChange={(e) => setCollectionForm({ ...collectionForm, description: e.target.value })}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Display Order</Label>
                          <Input
                            type="number"
                            min="1"
                            value={collectionForm.display_order}
                            onChange={(e) => setCollectionForm({ ...collectionForm, display_order: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Active Duration (days)</Label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="Leave empty for indefinite"
                            value={collectionForm.active_days}
                            onChange={(e) => setCollectionForm({ ...collectionForm, active_days: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowCollectionModal(false)}>Cancel</Button>
                      <Button 
                        onClick={createCollection} 
                        disabled={!collectionForm.name.trim() || collectionLoading}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {collectionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                        Create Collection
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {collections.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No collections yet</p>
                  <p className="text-sm">Create collections to group related deals</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {collections.map((collection) => (
                    <Card key={collection.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{collection.name}</h3>
                            <p className="text-sm text-gray-500 mt-1">{collection.description}</p>
                            <div className="flex items-center gap-2 mt-3">
                              <Badge variant="outline">{collection.item_count || 0} items</Badge>
                              <Badge variant="outline">Order: {collection.display_order}</Badge>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteCollection(collection.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {/* Preview Items */}
                        {collection.preview_items && collection.preview_items.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-gray-500 mb-2">Preview:</p>
                            <div className="flex gap-2 flex-wrap">
                              {collection.preview_items.map((item) => (
                                <Badge key={item.id} variant="secondary" className="text-xs">
                                  {item.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
